import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../components/common/ConfirmModal';
import { getOrgs, suspendOrg, activateOrg, bulkOrgAction, exportOrgsCSV } from '../utils/api';
import { formatDate, formatDistanceToNow } from '../utils/helpers';
import toast from 'react-hot-toast';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  Search, RefreshCw, Building2, CheckSquare, Square,
  ToggleLeft, ToggleRight, Download, ChevronDown, AlertTriangle, X, ChevronRight,
} from 'lucide-react';

const PLAN_OPTS   = ['trial', 'early_bird', 'core', 'pro', 'elite'];
const STATUS_OPTS = ['active', 'trialing', 'past_due', 'cancelled'];

export default function OrgsPage() {
  const navigate = useNavigate();
  const isMobile  = useIsMobile();
  const [orgs,       setOrgs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statFilter, setStatFilter] = useState('');
  const [busyId,     setBusyId]     = useState(null);

  // Bulk
  const [selected,    setSelected]    = useState(new Set());
  const [bulkAction,  setBulkAction]  = useState('');
  const [bulkPlan,    setBulkPlan]    = useState('pro');
  const [bulkBusy,    setBulkBusy]    = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showBulkMenu,setShowBulkMenu]= useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgs();
      setOrgs(data.organizations || []);
      setSelected(new Set());
    } catch { toast.error('Failed to load organizations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orgs.filter(o => {
    const q = search.toLowerCase();
    const matchQ = !q || o.name.toLowerCase().includes(q) || (o.owner_email || '').toLowerCase().includes(q);
    const matchP = !planFilter || o.subscription_plan === planFilter;
    const matchS = !statFilter ? true
      : statFilter === 'suspended' ? !o.is_active
      : o.subscription_status === statFilter;
    return matchQ && matchP && matchS;
  });

  // Expiring trials within 7 days
  const expiringTrials = orgs.filter(o => {
    if (!o.trial_ends_at) return false;
    const isTrial = o.subscription_plan === 'trial' || o.subscription_plan === 'trialing';
    if (!isTrial) return false;
    const days = Math.ceil((new Date(o.trial_ends_at) - new Date()) / (1000*60*60*24));
    return days >= 0 && days <= 7;
  });

  const handleToggle = async (org, e) => {
    e.stopPropagation();
    if (org.is_active) {
      setConfirmAction({
        title: 'Suspend Organization',
        message: `Suspend "${org.name}"? Their users will lose access immediately.`,
        confirmLabel: 'Suspend',
        danger: true,
        onConfirm: async () => {
          setBusyId(org.id);
          try { await suspendOrg(org.id); toast.success(`${org.name} suspended`); await load(); }
          catch (err) { toast.error(err.message || 'Failed'); }
          finally { setBusyId(null); }
        },
      });
      return;
    }
    setBusyId(org.id);
    try { await activateOrg(org.id); toast.success(`${org.name} reactivated`); await load(); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setBusyId(null); }
  };

  // Selection
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(o => o.id)));
  };
  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  // Bulk action
  const handleBulkAction = async (action, plan) => {
    if (!selected.size) return;
    const label = action === 'suspend' ? 'Suspend' : action === 'activate' ? 'Activate' : `Change plan to ${plan}`;
    setConfirmAction({
      title: 'Bulk Action',
      message: `${label} ${selected.size} organization(s)?`,
      confirmLabel: label,
      danger: action === 'suspend',
      onConfirm: async () => {
        setBulkBusy(true);
        setShowBulkMenu(false);
        try {
          const result = await bulkOrgAction({ org_ids: [...selected], action, plan });
          toast.success(`${result.updated} organization(s) updated`);
          await load();
        } catch (err) { toast.error(err.message || 'Bulk action failed'); }
        finally { setBulkBusy(false); }
      },
    });
  };

  // CSV Export (server-side — includes all fields)
  const handleExport = async () => {
    try {
      await exportOrgsCSV();
      toast.success('Organizations exported');
    } catch { toast.error('Export failed'); }
  };

  const trialDaysLeft = (o) => {
    if (!o.trial_ends_at) return null;
    return Math.ceil((new Date(o.trial_ends_at) - new Date()) / (1000*60*60*24));
  };

  const planBadge = (o) => ({
    trial:      <span className="badge badge-blue">Trial</span>,
    trialing:   <span className="badge badge-blue">Trial</span>,
    early_bird: <span className="badge badge-green">Early Bird</span>,
    core:       <span className="badge badge-dim">Core</span>,
    pro:        <span className="badge badge-gold">Pro</span>,
    elite:      <span className="badge badge-purple">Elite</span>,
    free:       <span className="badge badge-dim">Free</span>,
    starter:    <span className="badge badge-dim">Starter</span>,
    enterprise: <span className="badge badge-purple">Enterprise</span>,
  }[o.subscription_plan] || <span className="badge badge-dim">{o.plan_name || o.subscription_plan?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) || '—'}</span>);

  const statusBadge = (o) => {
    if (!o.is_active) return <span className="badge badge-red">Suspended</span>;
    return ({
      active:    <span className="badge badge-green">Active</span>,
      trialing:  <span className="badge badge-blue">Trial</span>,
      past_due:  <span className="badge badge-amber">Past Due</span>,
      cancelled: <span className="badge badge-dim">Cancelled</span>,
    }[o.subscription_status] || <span className="badge badge-dim">{o.subscription_status}</span>);
  };

  return (
    <Layout title="Organizations" crumb={`${filtered.length} shown`}>

      {/* ── Trial expiry alert banner ── */}
      {expiringTrials.length > 0 && (
        <div style={{
          background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 'var(--r-lg)', padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <AlertTriangle size={15} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--amber)', marginBottom: 4 }}>
              {expiringTrials.length} trial{expiringTrials.length > 1 ? 's' : ''} expiring within 7 days
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {expiringTrials.sort((a,b) => new Date(a.trial_ends_at) - new Date(b.trial_ends_at)).map(o => {
                const days = trialDaysLeft(o);
                return (
                  <button
                    key={o.id}
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(`/orgs/${o.id}`)}
                    style={{ borderColor: 'rgba(245,158,11,0.3)', color: 'var(--text-primary)', fontSize: '0.68rem' }}
                  >
                    {o.name}
                    <span style={{ color: days <= 2 ? 'var(--red)' : 'var(--amber)', marginLeft: 4, fontWeight: 700 }}>
                      {days === 0 ? 'today' : `${days}d`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="toolbar-inline" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-wrap" style={{ flex: 1, minWidth: 180, maxWidth: 300 }}>
          <Search size={13} />
          <input
            className="search-input"
            placeholder="Search firms or owner email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select className="form-select" style={{ width: 'auto', padding: '7px 12px' }}
          value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">All Plans</option>
          {PLAN_OPTS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
        </select>

        <select className="form-select" style={{ width: 'auto', padding: '7px 12px' }}
          value={statFilter} onChange={e => setStatFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="suspended">Suspended</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
        </select>

        <button className="btn btn-ghost btn-sm" onClick={handleExport} title="Export to CSV">
          <Download size={13} /> Export
        </button>
        <button className="btn btn-ghost btn-sm" onClick={load}>
          <RefreshCw size={13} />
        </button>
      </div>

      {/* ── Bulk action bar (appears when rows selected) ── */}
      {selected.size > 0 && (
        <div style={{
          background: 'var(--gold-dim)', border: '1px solid var(--border-gold)',
          borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gold)' }}>
            {selected.size} selected
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-success btn-sm" disabled={bulkBusy} onClick={() => handleBulkAction('activate')}>
            <ToggleRight size={12} /> Activate All
          </button>
          <button className="btn btn-danger btn-sm" disabled={bulkBusy} onClick={() => handleBulkAction('suspend')}>
            <ToggleLeft size={12} /> Suspend All
          </button>
          {/* Change plan dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={bulkBusy}
              onClick={() => setShowBulkMenu(s => !s)}
            >
              Change Plan <ChevronDown size={11} />
            </button>
            {showBulkMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)', overflow: 'hidden', zIndex: 20, minWidth: 130,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}>
                {PLAN_OPTS.map(p => (
                  <button
                    key={p}
                    onClick={() => { setShowBulkMenu(false); handleBulkAction('change_plan', p); }}
                    style={{
                      display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-primary)',
                    }}
                    onMouseEnter={e => e.target.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                  >
                    {p.charAt(0).toUpperCase()+p.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())} title="Clear selection">
            <X size={12} />
          </button>
        </div>
      )}

      {/* ── Table (desktop) / Cards (mobile) ── */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /> Loading organizations…</div>
      ) : !isMobile ? (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36, paddingRight: 0 }}>
                    <button
                      onClick={toggleAll}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                    >
                      {allSelected ? <CheckSquare size={14} style={{ color: 'var(--gold)' }} /> : <Square size={14} />}
                    </button>
                  </th>
                  <th>Firm</th>
                  <th>Owner</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="col-hide-mobile">Trial</th>
                  <th className="col-hide-mobile">Seats</th>
                  <th className="col-hide-mobile">Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(org => {
                  const days    = trialDaysLeft(org);
                  const isTrial = org.subscription_plan === 'trial' || org.subscription_plan === 'trialing' || org.subscription_status === 'trialing';
                  const expired = isTrial && days !== null && days <= 0;
                  const busy    = busyId === org.id;
                  const sel     = selected.has(org.id);

                  return (
                    <tr
                      key={org.id}
                      style={{ cursor: 'pointer', background: sel ? 'var(--gold-dim)' : undefined }}
                      onClick={() => navigate(`/orgs/${org.id}`)}
                    >
                      <td style={{ paddingRight: 0 }} onClick={e => toggleSelect(org.id, e)}>
                        {sel
                          ? <CheckSquare size={14} style={{ color: 'var(--gold)', display: 'block' }} />
                          : <Square size={14} style={{ color: 'var(--text-muted)', display: 'block' }} />}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className={`pulse-dot ${!org.is_active ? 'red' : org.subscription_status === 'past_due' ? 'amber' : 'green'}`} />
                          <div>
                            <div style={{ fontWeight: 600 }}>{org.name}</div>
                            <div className="uuid">{org.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.8rem' }}>{org.owner_name}</div>
                        <div className="mono-sm">{org.owner_email}</div>
                      </td>
                      <td>{planBadge(org)}</td>
                      <td>{statusBadge(org)}</td>
                      <td className="col-hide-mobile">
                        {isTrial && days !== null ? (
                          <span style={{
                            fontFamily: 'var(--font-mono)', fontSize: '0.68rem', fontWeight: 700,
                            color: expired ? 'var(--red)' : days <= 3 ? 'var(--amber)' : 'var(--text-muted)',
                          }}>
                            {expired ? 'Expired' : `${days}d`}
                          </span>
                        ) : <span className="mono-sm">—</span>}
                      </td>
                      <td className="mono col-hide-mobile">{org.user_count} / {org.seats_purchased}</td>
                      <td className="mono-sm col-hide-mobile">{formatDistanceToNow(org.created_at)}</td>
                      <td>
                        <button
                          className={`btn btn-sm ${org.is_active ? 'btn-danger' : 'btn-success'}`}
                          disabled={busy}
                          onClick={e => handleToggle(org, e)}
                          title={org.is_active ? 'Suspend' : 'Reactivate'}
                        >
                          {busy
                            ? <span className="spinner" style={{ width: 12, height: 12 }} />
                            : org.is_active ? <ToggleLeft size={13} /> : <ToggleRight size={13} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9}>
                    <div className="empty">
                      <Building2 size={28} />
                      <h3>No firms found</h3>
                      <p>Try adjusting your search or filters.</p>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ── Mobile card list (replaces table on small screens) ── */}
      {!loading && isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.length === 0 ? (
            <div className="empty"><Building2 size={28} /><h3>No firms found</h3><p>Adjust your search or filters.</p></div>
          ) : filtered.map(org => {
            const days    = trialDaysLeft(org);
            const isTrial = org.subscription_plan === 'trial' || org.subscription_plan === 'trialing' || org.subscription_status === 'trialing';
            const expired = isTrial && days !== null && days <= 0;
            const busy    = busyId === org.id;
            return (
              <div key={org.id} className="card" style={{ padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }}
                onClick={() => navigate(`/orgs/${org.id}`)}>
                <div className={`pulse-dot ${!org.is_active ? 'red' : org.subscription_status === 'past_due' ? 'amber' : 'green'}`} style={{ marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>{org.name}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {planBadge(org)}{statusBadge(org)}
                    {isTrial && days !== null && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', fontWeight: 700, color: expired ? 'var(--red)' : days <= 3 ? 'var(--amber)' : 'var(--text-muted)' }}>
                        {expired ? 'Expired' : `${days}d left`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{org.owner_email}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                    {org.user_count}/{org.seats_purchased} seats
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button className={`btn btn-sm ${org.is_active ? 'btn-danger' : 'btn-success'}`}
                    disabled={busy} onClick={e => handleToggle(org, e)}>
                    {busy ? <span className="spinner" style={{ width: 12, height: 12 }} />
                      : org.is_active ? <ToggleLeft size={13} /> : <ToggleRight size={13} />}
                  </button>
                  <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />

    </Layout>
  );
}
